import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import './ErrorBoundary.css'

/**
 * The last thing between a render error and a blank white page (#95).
 *
 * React 18 unmounts the WHOLE tree when a render throws and nothing catches it.
 * Not the failing panel — the app. The customer gets a white page: no message,
 * no navigation, nothing to read out to support, and nothing that distinguishes
 * it from a failed deploy or a dead network.
 *
 * This repo throws during render on purpose, and it does it where money is
 * rendered: `formatMoney`, `moneyFromDTO`, `moneyToDTO` and `sum` all refuse
 * rather than produce a figure they cannot stand behind, and #93 widened the
 * first of those to any currency this bundle holds no exponent for. Every one of
 * those refusals is right. Each of them currently lands as a white page, which
 * is a strictly worse way to tell somebody something is wrong than a sentence.
 *
 * # This is a class component, and it has to be
 *
 * There is no hook equivalent. `getDerivedStateFromError` and `componentDidCatch`
 * are the only API React exposes for this, and they exist on classes only.
 *
 * # What it deliberately does not do
 *
 * It does not let rendering continue past a bad value. A screen drawn with one
 * figure quietly missing is the failure the throw exists to prevent, so the
 * fallback replaces its scope entirely rather than patching a hole in it.
 *
 * It does not catch errors in event handlers, promises or `setTimeout` — React
 * boundaries never have. Copy that implied otherwise would be worse than the
 * gap, so none of it does.
 */

/**
 * `app` replaces everything and offers a reload. `panel` replaces one routed
 * screen and leaves the rail, so a customer can still navigate and sign out —
 * which is a materially different situation from looking at nothing.
 */
type Scope = 'app' | 'panel'

interface Props {
  readonly scope: Scope
  readonly children: ReactNode
}

interface State {
  readonly error: Error | null
}

/**
 * What a customer is told, and why it is not "no money has moved".
 *
 * The obvious reassurance is that nothing was submitted. **It is not something
 * this code knows.** A render error is a failure to DISPLAY, and it can happen
 * immediately after a successful instruction — while drawing the response to it.
 * Asserting "nothing was submitted" would therefore be a confident statement
 * about somebody's money made without checking, which is the #22 mistake exactly.
 *
 * So it says the true thing instead, and it is more useful anyway: the fault is
 * in displaying, it does not by itself change anything, and if something HAD
 * just been submitted this screen cannot say whether it completed — so go and
 * look. That answers the actual fear (*did I just send money?*) with a way to
 * find out rather than with a reassurance nobody verified.
 *
 * # It does not name where to look, and that was a bug for a few hours (#109)
 *
 * The panel version said "check Balances", which the app cannot honour twice
 * over: Balances renders no monetary figure and no activity — `Account` has no
 * amount field at all — and nothing inside the panel boundary submits anything
 * it would reflect, since both POSTs live on `/welcome`, which is routed outside
 * the shell. A smaller fault than the sentence this file refuses to write, and
 * the same class of fault. When a screen exists that can answer "did it go
 * through", naming it here becomes correct; until then it must not pretend.
 *
 * A plain function rather than a component, and called rather than rendered: it
 * holds no state, uses no hooks and has no identity worth reconciling. Both
 * scopes now share every word, which is the honest shape — what differs between
 * them is what gets REPLACED, not what is true.
 */
function wording(): ReactNode {
  return (
    <>
      <p>This is a fault in the app, not in your account.</p>
      <p>
        It does not by itself change anything. But if you had just submitted something, this
        screen cannot tell you whether it went through — reload and check before trying it again.
      </p>
    </>
  )
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  /**
   * Report it. A boundary that swallows the error silently has traded a visible
   * failure for an invisible one, which is the trade `go-wallet#429` refuses for
   * the journal sweep and the same argument applies here.
   *
   * `console.error` is the whole of the reporting, because it is the whole of
   * what exists — nothing in this app collects browser errors. **That is also
   * why the fallback shows no support reference.** A reference nobody can look
   * up is worse than none: it invites a customer to quote a number to somebody
   * who has no way to find it. When an error reporter is added, this is where it
   * goes and the reference becomes real at the same moment.
   */
  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[tesora] render failed:', error, info.componentStack)
  }

  private readonly retry = (): void => {
    this.setState({ error: null })
  }

  private readonly reload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    if (this.props.scope === 'app') {
      return (
        <div className="boot boot--failed" role="alert">
          <span className="boot__mark">TESORA</span>
          <h1>Something went wrong displaying this page.</h1>
          {wording()}
          <p className="efail__actions">
            <button type="button" className="efail__cta" onClick={this.reload}>
              Reload
            </button>
          </p>
        </div>
      )
    }

    return (
      <section className="efail" role="alert">
        <h2 className="efail__title">This part of the page could not be displayed.</h2>
        {wording()}
        <p className="efail__actions">
          {/*
            "Try again" re-renders the same screen. It is worth offering because
            a render error can come from data that has since changed, and it
            costs a click to find out. It is NOT presented as a fix — if it fails
            again the customer gets this screen again, which is the honest
            outcome.
          */}
          <button type="button" className="efail__cta" onClick={this.retry}>
            Try again
          </button>
          <button type="button" className="efail__ghost" onClick={this.reload}>
            Reload the page
          </button>
        </p>
        <p className="efail__hint">The menu is still working, so you can go somewhere else.</p>
      </section>
    )
  }
}
